import React, { useEffect, useRef, useState } from 'react';
import { Target, Award, TrendingUp, Briefcase, Lightbulb, GraduationCap, Shield } from 'lucide-react';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

  .ent-w *, .ent-w *::before, .ent-w *::after {
    font-family: 'Outfit', sans-serif;
    box-sizing: border-box;
  }
  .ent-w h1, .ent-w h2, .ent-w h3, .ent-w h4, .ent-w h5, .ent-w h6 {
    font-family: 'Cormorant Garamond', serif;
  }

  .ent-w {
    --navy:       #0A1535;
    --navy-mid:   #0D1D45;
    --blue:       #1B4FD8;
    --blue-mid:   #2563EB;
    --blue-bright:#3B7BFF;
    --gold:       #D4A017;
    --ice:        #F0F5FF;
    --ice-mid:    #DBEAFE;
    --border:     #E2E8F0;
    --text:       #1E293B;
    --muted:      #64748B;
    --dim:        #94A3B8;
    --white:      #FFFFFF;
    font-family: 'Outfit', sans-serif;
  }

  .ent-w-section {
    background: var(--white);
    position: relative; overflow: hidden; padding: 112px 0;
  }

  .ent-w-grid {
    position: absolute; inset: 0; pointer-events: none; z-index: 0;
    background-image:
      linear-gradient(rgba(59,123,255,0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(59,123,255,0.035) 1px, transparent 1px);
    background-size: 52px 52px;
  }

  .ent-w-wash-l {
    position: absolute; top: 0; left: -200px;
    width: 600px; height: 600px; border-radius: 50%;
    background: radial-gradient(circle, rgba(59,123,255,0.055) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }
  .ent-w-wash-r {
    position: absolute; bottom: -100px; right: -100px;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(212,160,23,0.04) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }

  .ent-w-topline {
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, transparent, var(--blue-bright) 40%, var(--gold) 70%, transparent);
  }

  .ent-w-inner { max-width: 1280px; margin: 0 auto; padding: 0 64px; position: relative; z-index: 2; }
  .ent-w-layout { display: grid; grid-template-columns: 500px 1fr; gap: 88px; align-items: start; margin-bottom: 96px; }

  .ent-w-img-sticky { position: sticky; top: 100px; }
  .ent-w-img-outer { position: relative; }
  .ent-w-img-num {
    position: absolute; top: -55px; left: -10px;
    font-family: 'Cormorant Garamond', serif; font-size: 160px; font-weight: 600;
    color: rgba(10,21,53,0.04); line-height: 1;
    pointer-events: none; user-select: none;
  }
  .ent-w-img-box {
    position: relative; overflow: hidden; z-index: 1;
    box-shadow: 0 40px 100px rgba(10,21,53,0.14), 0 0 0 1px rgba(37,99,235,0.08);
  }
    .ent-w-img-box img { width: 100%; display: block; height: 580px; object-fit: cover; }
    @media (max-width: 640px) {
      .ent-w-img-box img {
        height: 220px;
        object-fit: cover;
      }
    }
  .ent-w-img-grad {
    position: absolute; inset: 0;
    background: linear-gradient(to bottom, rgba(10,21,53,0.03) 0%, transparent 35%, rgba(10,21,53,0.30) 100%);
  }
  .ent-w-bk-tr { position: absolute; top: -10px; right: -10px; z-index: 2; width: 36px; height: 36px; border-top: 2px solid var(--gold); border-right: 2px solid var(--gold); }
  .ent-w-bk-bl { position: absolute; bottom: -10px; left: -10px; z-index: 2; width: 36px; height: 36px; border-bottom: 2px solid var(--blue-mid); border-left: 2px solid var(--blue-mid); }

  .ent-w-img-chip {
    position: absolute; bottom: 20px; left: 16px; right: 16px; z-index: 3;
    background: rgba(10,21,53,0.85); backdrop-filter: blur(18px);
    border: 1px solid rgba(59,123,255,0.2);
    padding: 14px 16px; display: flex; align-items: center; gap: 14px;
  }
  .ent-w-chip-icon {
    width: 36px; height: 36px; flex-shrink: 0;
    background: rgba(59,123,255,0.15); border: 1px solid rgba(59,123,255,0.3);
    display: flex; align-items: center; justify-content: center;
  }
  .ent-w-chip-title { font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 600; color: white; letter-spacing: 0.05em; text-transform: uppercase; }
  .ent-w-chip-sub { font-family: 'Outfit', sans-serif; font-size: 10px; color: rgba(255,255,255,0.5); margin-top: 2px; }

  .ent-w-chapter { display: flex; align-items: center; gap: 12px; margin-bottom: 28px; }
  .ent-w-dash { width: 36px; height: 2px; background: var(--blue-bright); }
  .ent-w-label { font-family: 'Outfit', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 0.30em; text-transform: uppercase; color: var(--blue-mid); display: flex; align-items: center; gap: 8px; }

  .ent-w-h2 {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(30px, 3vw, 58px);
    font-weight: 600; line-height: 1.08; letter-spacing: -0.02em;
    color: var(--navy); margin: 0 0 6px;
  }
  .ent-w-h2 em { font-style: italic; font-weight: 500; color: var(--blue-bright); }
  .ent-w-h2-sub { font-family: 'Outfit', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: var(--dim); margin-bottom: 36px; display: flex; align-items: center; gap: 10px; }
  .ent-w-h2-sub::before { content: ''; flex: 0 0 18px; height: 1px; background: var(--border); }

  .ent-w-features { display: flex; flex-direction: column; gap: 10px; }

  .ent-w-feat {
    display: flex; align-items: flex-start; gap: 16px;
    padding: 20px 22px;
    background: var(--white); border: 2px solid var(--border);
    cursor: pointer;
    transition: border-color 0.3s ease, background 0.3s ease, transform 0.25s ease, box-shadow 0.3s ease;
    position: relative;
  }
  .ent-w-feat-bar {
    position: absolute; left: 0; top: 0; bottom: 0; width: 0;
    background: linear-gradient(180deg, var(--blue-mid), var(--blue-bright));
    transition: width 0.3s ease;
  }
  .ent-w-feat.active .ent-w-feat-bar,
  .ent-w-feat:hover .ent-w-feat-bar { width: 4px; }
  .ent-w-feat.active,
  .ent-w-feat:hover {
    border-color: var(--blue-mid); background: var(--ice);
    transform: translateX(4px); box-shadow: 0 4px 24px rgba(37,99,235,0.08);
  }
  .ent-w-feat-icon {
    width: 42px; height: 42px; flex-shrink: 0;
    background: var(--ice); border: 1px solid var(--ice-mid);
    display: flex; align-items: center; justify-content: center;
    transition: background 0.3s ease, border-color 0.3s ease;
  }
  .ent-w-feat.active .ent-w-feat-icon,
  .ent-w-feat:hover .ent-w-feat-icon { background: var(--blue-mid); border-color: var(--blue-mid); }
  .ent-w-feat-title { font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 600; color: var(--navy); margin-bottom: 7px; letter-spacing: 0.02em; }
  .ent-w-feat-desc { font-family: 'Outfit', sans-serif; font-size: 13px; line-height: 1.72; color: var(--muted); }

  .ent-w-stats-wrap { border-top: 2px solid var(--border); padding-top: 88px; }

  .ent-w-divider { display: flex; align-items: center; gap: 20px; margin-bottom: 52px; }
  .ent-w-divider-line { flex: 1; height: 1px; background: var(--border); }
  .ent-w-divider-label { font-family: 'Outfit', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 0.28em; text-transform: uppercase; color: var(--gold); display: flex; align-items: center; gap: 8px; white-space: nowrap; }

  .ent-w-stats-head { text-align: center; margin-bottom: 48px; }
  .ent-w-stats-h {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(32px, 4vw, 52px); font-weight: 600;
    color: var(--navy); letter-spacing: -0.02em; margin-bottom: 12px;
  }
  .ent-w-stats-h em { font-style: italic; font-weight: 500; color: var(--blue-bright); }
  .ent-w-stats-sub { font-family: 'Outfit', sans-serif; font-size: 15px; color: var(--muted); max-width: 440px; margin: 0 auto; }

  .ent-w-stat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 16px; }

  .ent-w-stat {
    background: var(--white); border: 2px solid var(--border);
    padding: 36px 24px; text-align: center; position: relative; overflow: hidden;
    transition: border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
  }
  .ent-w-stat:hover { border-color: var(--blue-mid); transform: translateY(-6px); box-shadow: 0 20px 56px rgba(37,99,235,0.1); }
  .ent-w-stat::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--blue-mid), var(--blue-bright));
    transform: scaleX(0); transform-origin: left; transition: transform 0.35s ease;
  }
  .ent-w-stat:hover::before { transform: scaleX(1); }
  .ent-w-stat-corner {
    position: absolute; top: 0; right: 0;
    width: 60px; height: 60px;
    background: radial-gradient(circle at top right, rgba(59,123,255,0.06), transparent 70%);
  }
  .ent-w-stat-icon {
    width: 52px; height: 52px; margin: 0 auto 18px;
    background: var(--navy); display: flex; align-items: center; justify-content: center;
    box-shadow: 0 8px 24px rgba(10,21,53,0.18);
  }
  .ent-w-stat-num {
    font-family: 'Cormorant Garamond', serif;
    font-size: 52px; font-weight: 600; line-height: 1;
    color: var(--navy); margin-bottom: 8px; letter-spacing: -0.02em;
  }
  .ent-w-stat-num span { color: var(--blue-mid); }
  .ent-w-stat-label { font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 600; color: var(--navy); margin-bottom: 4px; letter-spacing: 0.06em; text-transform: uppercase; }
  .ent-w-stat-sub { font-family: 'Outfit', sans-serif; font-size: 11px; color: var(--dim); }
  .ent-w-stat-bar { margin-top: 18px; height: 3px; background: var(--border); overflow: hidden; }
  .ent-w-stat-fill { height: 100%; background: linear-gradient(90deg, var(--blue-mid), var(--blue-bright)); transition: width 1.5s cubic-bezier(0.16,1,0.3,1); }

  .ent-w-trust {
    background: var(--navy); padding: 26px 32px;
    display: flex; align-items: center; gap: 20px;
    border-left: 4px solid var(--gold);
    box-shadow: 0 16px 48px rgba(10,21,53,0.12);
  }
  .ent-w-trust-icon {
    width: 46px; height: 46px; flex-shrink: 0;
    background: rgba(212,160,23,0.15); border: 1px solid rgba(212,160,23,0.25);
    display: flex; align-items: center; justify-content: center;
  }
  .ent-w-trust-title { font-family: 'Outfit', sans-serif; font-size: 15px; font-weight: 600; color: white; margin-bottom: 6px; }
  .ent-w-trust-text { font-family: 'Outfit', sans-serif; font-size: 13px; line-height: 1.68; color: rgba(255,255,255,0.65); }

  .ent-w-fade { opacity: 0; transform: translateY(28px); transition: opacity 0.8s ease, transform 0.8s ease; }
  .ent-w-fade.on { opacity: 1; transform: none; }
  .ent-w-fade-l { opacity: 0; transform: translateX(-36px); transition: opacity 0.85s ease, transform 0.85s ease; }
  .ent-w-fade-l.on { opacity: 1; transform: none; }

  @media (max-width: 1100px) {
    .ent-w-layout { grid-template-columns: 1fr; gap: 56px; }
    .ent-w-inner { padding: 0 28px; }
    .ent-w-img-sticky { position: static; }
    .ent-w-stat-grid { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 640px) {
    .ent-w-section { padding: 80px 0; }
    .ent-w-stat-grid {
      grid-template-columns: repeat(3, 1fr);
      display: flex;
      overflow-x: auto;
      gap: 12px;
      padding-bottom: 8px;
    }
    .ent-w-stat {
      min-width: 220px;
      flex: 0 0 auto;
      padding: 22px 10px;
      font-size: 18px;
    }
    .ent-w-stat-num {
      font-size: 32px;
      margin-bottom: 4px;
    }
    .ent-w-stat-label {
      font-size: 11px;
    }
    .ent-w-stat-sub {
      font-size: 10px;
    }
    .ent-w-stats-head {
      margin-bottom: 28px;
    }
  }
`;

const features = [
  { icon: Lightbulb, title: 'Innovative Solutions', desc: 'Entrepreneurship begins with the right mindset, not necessarily with large capital. With the right guidance and system, anyone can start small and build meaningful growth over time.' },
  { icon: GraduationCap, title: 'Winning Expertise', desc: 'As artificial intelligence transforms traditional roles, practical skills and real-world experience have become more important than ever.Our Student Internship Programme empowers young minds with hands-on exposure, helping them develop confidence, skills, and career readiness.' },
  { icon: Shield, title: 'Dedicated Support', desc: 'We provide a rewarding compensation plan supported by innovative products and our Green Planet initiative, creating a foundation for sustainable growth and long-term entrepreneurial success.' },
];

const EntrepreneurshipSection = () => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  const [active, setActive] = useState(0);
  const [counts, setCounts] = useState({ a: 0, b: 0 });

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); animateCount(); }
    }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!vis) return;
    const t = setInterval(() => setActive(p => (p + 1) % 3), 3000);
    return () => clearInterval(t);
  }, [vis]);

  const animateCount = () => {
    const steps = 80; const dur = 2400; let c = 0;
    const t = setInterval(() => {
      c++;
      setCounts({ a: Math.min(Math.floor((106 / steps) * c), 106), b: Math.min(Math.floor((1000 / steps) * c), 1000) });
      if (c >= steps) clearInterval(t);
    }, dur / steps);
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <section className="ent-w ent-w-section" ref={ref}>
        <div className="ent-w-grid" />
        <div className="ent-w-wash-l" />
        <div className="ent-w-wash-r" />
        <div className="ent-w-topline" />

        <div className="ent-w-inner">
          <div className="ent-w-layout">

            {/* Left: image */}
            <div className={`ent-w-fade-l ${vis ? 'on' : ''}`}>
              <div className="ent-w-img-sticky">
                <div className="ent-w-img-outer">
                    <div className="ent-w-img-num" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600 }}>02</div>
                  <div className="ent-w-bk-tr" /><div className="ent-w-bk-bl" />
                  <div className="ent-w-img-box">
                    <img src="/assest/entrepreneur.jpeg" alt="Entrepreneurship" />
                    <div className="ent-w-img-grad" />
                    <div className="ent-w-img-chip">
                      <div className="ent-w-chip-icon"><Briefcase size={16} color="#3B7BFF" /></div>
                      <div>
                        <div className="ent-w-chip-title">12 Years of Trust</div>
                        <div className="ent-w-chip-sub">Proven · Legal · Transparent</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: content */}
            <div className={`ent-w-fade ${vis ? 'on' : ''}`} style={{ transitionDelay: '220ms' }}>
              <div className="ent-w-chapter">
                <div className="ent-w-dash" />
                <span className="ent-w-label"><Briefcase size={11} color="#2563EB" /> Reasons to Choose Us</span>
              </div>
              <h2 className="ent-w-h2">Trusted solutions for your <em>Entrepreneurship.</em></h2>
              <p className="ent-w-h2-sub">Why Vibrant Viva</p>

              <div className="ent-w-features">
                {features.map((f, i) => {
                  const Icon = f.icon;
                  const isActive = active === i;
                  return (
                    <div key={i} className={`ent-w-feat ${isActive ? 'active' : ''}`} onMouseEnter={() => setActive(i)}>
                      <div className="ent-w-feat-bar" />
                      <div className="ent-w-feat-icon">
                        <Icon size={20} color={isActive ? '#fff' : '#2563EB'} />
                      </div>
                      <div>
                        <div className="ent-w-feat-title">{f.title}</div>
                        <p className="ent-w-feat-desc">{f.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className={`ent-w-stats-wrap ent-w-fade ${vis ? 'on' : ''}`} style={{ transitionDelay: '400ms' }}>
            <div className="ent-w-divider">
              <div className="ent-w-divider-line" />
              <div className="ent-w-divider-label"><Award size={11} color="#D4A017" /> Our Achievement</div>
              <div className="ent-w-divider-line" />
            </div>

            <div className="ent-w-stats-head">
              <h3 className="ent-w-stats-h">Proven <em>Track Record</em></h3>
              <p className="ent-w-stats-sub">Our business stands on a foundation of trust, regulatory compliance, and unwavering transparency.</p>
            </div>

            <div className="ent-w-stat-grid">
              {[
                { Icon: Award,      num: counts.a, prefix: '',   suffix: '',  label: 'crorepatis Crossed', sub: 'Industry leaders built',  fill: `${(counts.a/106)*100}%` },
                { Icon: TrendingUp, num: counts.b, prefix: '₹', suffix: '+', label: 'Crore Earnings Milestone', sub: 'In commissions paid',     fill: `${(counts.b/1000)*100}%` },
                { Icon: Target,     num: 3,        prefix: '',   suffix: '',  label: 'Days Processing',    sub: 'Fast payment turnaround', fill: '100%' },
              ].map((s, i) => (
                <div className="ent-w-stat" key={i}>
                  <div className="ent-w-stat-corner" />
                  <div className="ent-w-stat-icon"><s.Icon size={22} color="white" /></div>
                  <div className="ent-w-stat-num" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600 }}>{s.prefix}{s.num}<span>{s.suffix}</span></div>
                  <div className="ent-w-stat-label">{s.label}</div>
                  <div className="ent-w-stat-sub">{s.sub}</div>
                  <div className="ent-w-stat-bar">
                    <div className="ent-w-stat-fill" style={{ width: vis ? s.fill : '0%' }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="ent-w-trust">
              <div className="ent-w-trust-icon"><Shield size={22} color="#D4A017" /></div>
              <div>
                <div className="ent-w-trust-title">12 Years of Trust & Transparency</div>
                <p className="ent-w-trust-text">Vibrant Viva operates in accordance with applicable laws including the Direct Selling Rules of India, maintaining transparency in operations, product distribution, and compensation structure.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default EntrepreneurshipSection;